/**
 * Authentication API endpoints.
 *
 * Endpoints:
 * - POST /api/web-admin/auth/login - Login with username/password
 * - POST /api/web-admin/auth/logout - Logout current session
 * - GET /api/web-admin/auth/me - Get current user info
 */

import { api } from './client';

/**
 * User type returned from auth endpoints.
 */
export interface User {
  id: string;
  username: string;
  display_name: string;
  role: string;
  role_label: string;
}

/**
 * Login request payload.
 */
interface LoginRequest {
  username: string;
  password: string;
}

/**
 * Login response from the server.
 */
export interface AuthResponse {
  request_id: string;
  user: User;
}

/**
 * Logout response from the server.
 */
export interface LogoutResponse {
  request_id: string;
  success: boolean;
}

/**
 * Authentication API methods.
 */
export const authApi = {
  /**
   * Login with username and password.
   * Sets session cookie on success.
   */
  login: async (username: string, password: string): Promise<AuthResponse> => {
    const payload: LoginRequest = { username, password };
    return api.webAdmin.post<AuthResponse>('/auth/login', payload);
  },

  /**
   * Logout current session.
   * Clears session cookie.
   */
  logout: async (): Promise<LogoutResponse> => {
    return api.webAdmin.post<LogoutResponse>('/auth/logout');
  },

  /**
   * Get current authenticated user.
   * Returns user info if authenticated, throws 401 if not.
   */
  me: async (options?: { signal?: AbortSignal }): Promise<AuthResponse> => {
    return api.webAdmin.get<AuthResponse>('/auth/me', { signal: options?.signal ?? null });
  },
};
